/**
 * Compliance copy, verbatim from the phone's `app_strings.dart` (`Disclaimers` and friends) — it has
 * been through the copy and compliance review, and the two clients must say the same thing
 * (§22.1). Each constant names its Dart source; change both together.
 */
export const DISCLAIMERS = {
  /** `Disclaimers.backtestResult` — under every result. */
  backtestResult: 'Historical simulation only — not indicative of future results.',
  /** `Disclaimers.ranking` — under Refine's ranking. */
  ranking:
    'The ranking does not consider your financial circumstances, objectives or risk tolerance, and does not assess whether any strategy or financial instrument is suitable for you. Results are simulated historical performance and do not constitute a recommendation to trade any strategy or instrument. Simulated past performance is not a reliable indicator of future performance.',
  /** The home-screen risk line. */
  monitoring: 'Monitoring tool only — not financial advice. Trading carries risk of loss.',
  /** `Disclaimers.billing` — under every place a subscription is sold. */
  billing:
    'By subscribing, you agree that subscriptions auto-renew unless you cancel. The exact charge is shown at purchase. Cancel anytime — manage your subscription through the platform you subscribed on.',
  /** `Disclaimers.alertFallback` — when an alert has no served disclaimer. */
  alertFallback:
    'This alert reports that conditions you defined occurred. It is not a recommendation to buy or sell. SixOne8 does not provide investment advice. Trading carries risk of loss.',
  /** `Disclaimers.backtestOptionalAsRequired`. */
  optionalAsRequired:
    'Optional conditions were evaluated as required. This is a simulation of past data and does not describe future results.',
} as const;

export const TESTING_CARDS = {
  /** `TestingText.backtestCard*` and friends. */
  backtest: {
    title: 'Backtest',
    body: "Your full strategy against history — your exits, your broker's costs, and every trade it would have opened and closed.",
  },
  entryCheck: {
    title: 'Entry check',
    body: 'When your conditions fire, which direction does the market actually move?',
  },
  refine: {
    title: 'Refine',
    body: 'Test variations of a strategy you have already built and see how each one scores against your original.',
  },
  // Not in the phone app yet: written here to the same rule (what the tool does, never outcomes).
  parameterSearch: {
    title: 'Parameter search',
    body: 'Change one, two or three settings across a grid and see how the result moves — and whether the best cell sits on a plateau or a spike.',
  },
} as const;

/** `ReferralText` (6e), verbatim. */
export const REFERRAL = {
  haveACodeBody: "A promo code or a friend's referral code. Add your code before you subscribe.",
  howItWorks: (credits: number) =>
    `Friends get 50% off their first month on a monthly plan. When a friend subscribes, you get ${credits} credits in your wallet — they never expire.`,
  rewardsSoFar: (referred: number, subscribed: number, credits: number) =>
    `${referred} referred · ${subscribed} subscribed · ${credits} credits earned`,
  copied: 'Code copied',
} as const;

/** `StrategyText` AI strings, verbatim. */
export const AI = {
  buildOne: 'Describe it to AI',
  builderIntro: 'Describe the setup you look for. The assistant turns it into conditions — you review and edit every one before saving.',
  builderHint: 'Describe your strategy...',
  generate: 'Generate strategy',
  generating: 'Generating your strategy...',
  cost: 'Uses 15 credits — 5 if nothing can be built from your description.',
  refusalTitle: 'Some of that could not be expressed',
  noStrategy: 'That description could not be turned into conditions this app can evaluate. Try describing what has to be true on the chart, and on which timeframe.',
  drafted: 'Drafted from your description. Review the conditions, then create the strategy.',
  describeAgain: 'Describe it differently',
  describeAgainNote: 'Generating again replaces the conditions below, including any changes you made.',
  assumptionsHeader: 'Chosen for you',
  assumptionsIntro: 'Your description did not cover everything a strategy needs, so these were filled in. Change any of them below.',
  reviewAction: 'Review with AI',
  reviewTitle: 'AI review',
  reviewCost: 'Uses 10 credits',
  reviewStale: 'This strategy has been edited since this review. Request a fresh one to cover the current conditions.',
  reviewRefresh: 'Request a fresh review',
} as const;

/**
 * `StrategyText.preview*`, verbatim: the Elite per-condition breakdown under the builder's match
 * estimate (`features/builder/PreviewBreakdown.tsx`). What was measured over the window, never
 * advice or an outcome.
 */
export const PREVIEW_BREAKDOWN = {
  heading: 'Condition by condition',
  held: (pct: string) => `True ${pct}% of the time`,
  soleBlocker: (pct: string) => `Only thing missing ${pct}% of the time`,
  optional: 'Optional',
  topBlocker: 'Most often the only thing missing',
  /** The heading's ⓘ: what is counted, and why the figures do not sum to 100. */
  info: [
    'The preview checks your strategy at every bar close over its window.',
    '"True" is how often this condition held on its own, whatever the others were doing.',
    '"Only thing missing" is how often every other required condition held and this one did not — the moments it alone stopped a match. The condition missing most often is the one to look at first when a strategy rarely matches.',
    'Each figure is measured separately, so they do not add up to 100.',
  ],
} as const;

/**
 * `TestingText.outOfCredits*`, verbatim: the prompt a Free account sees when it tries to spend
 * credits it does not have (`features/testing/CreditsUpgrade.tsx`). Allowances, never outcomes.
 */
export const CREDITS_UPGRADE = {
  title: 'Not enough credits',
  plans: (pro: string, elite: string) => `Pro includes ${pro} credits a week and Elite ${elite}.`,
  plansUnpriced: 'Paid plans include more credits every week.',
  alternative: 'Entry checks start at 1 credit and 90-day backtests at 2, and your credits refill in full every week.',
  /** The pre-submit sentence, in the server refusal's own words. */
  shortfall: (cost: number, available: string) => `This run costs ${cost === 1 ? '1 credit' : `${cost.toLocaleString('en-GB')} credits`}; you have ${available} left.`,
} as const;
